import { OpenInNew } from '@mui/icons-material';
import { Autocomplete, Avatar, Box, Button, Chip, Paper, TextField, Typography } from '@mui/material';
import { useMemo } from 'react';
import { Controller, useFormContext, useWatch } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { useGetAllRecognitionsByLicenseQuery } from 'services/Recognition/hooks/useQuery';
import { IRecognitionSimpleProps } from 'services/Recognition/types';
import { SchoolClassFormData } from 'services/SchoolClass/schemas';
import { stringAvatar } from './utils/stringAvatar';

const StudentsCountHeader = () => {
  const selectedIds = useWatch<SchoolClassFormData, 'recognitionsId'>({ name: 'recognitionsId' }) || [];
  return (
    <Typography variant="subtitle1" fontWeight="bold" color="text.primary">
      Alunos Vinculados ({selectedIds.length})
    </Typography>
  );
};

export function StudentsCard() {
  const navigate = useNavigate();
  const { control, formState: { errors } } = useFormContext<SchoolClassFormData>();

  const { data: studentsList = [], isLoading: isLoadingStudents } = useGetAllRecognitionsByLicenseQuery(
    { pageNumber: 1, pageSize: 2000 },
    {}
  );

  const studentsMap = useMemo(() => {
    const map = new Map<number, IRecognitionSimpleProps>();
    
    studentsList.forEach((student) => {
      if (student.id !== undefined) { 
        map.set(student.id, student);
      }
    });
    
    return map;
  }, [studentsList]);

  return (
    <Paper elevation={0} sx={{ p: 4, borderRadius: 3, border: '1px solid', borderColor: 'divider', bgcolor: 'background.paper' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <StudentsCountHeader />
      </Box>

      <Controller
        name="recognitionsId"
        control={control}
        render={({ field }) => (
          <Autocomplete
            multiple
            options={studentsList}
            loading={isLoadingStudents}
            loadingText="Buscando alunos..."
            getOptionLabel={(option: IRecognitionSimpleProps) => option.name || ''}
            value={(field.value || [])
              .map((id) => studentsMap.get(id))
              .filter(Boolean) as IRecognitionSimpleProps[]}
            onChange={(_, data) => field.onChange(data.map((opt) => opt.id))}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            renderTags={(tagValue, getTagProps) =>
              tagValue.map((option, index) => {
                const { key, ...tagProps } = getTagProps({ index });
                return (
                  <Chip
                    key={key}
                    {...tagProps}
                    avatar={option.image ? <Avatar src={option.image as string} /> : <Avatar {...stringAvatar(option.name)} />}
                    label={option.name}
                    variant="outlined"
                    sx={{ borderColor: 'divider', color: 'text.primary' }}
                  />
                );
              })
            }
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={(field.value || []).length === 0 && !isLoadingStudents ? 'Buscar aluno...' : ''}
                error={!!errors.recognitionsId}
                helperText={errors.recognitionsId?.message}
                sx={{ '& .MuiInputBase-root': { bgcolor: 'background.default' } }}
              />
            )}
          />
        )}
      />

      <Button
        size="small"
        color="inherit"
        endIcon={<OpenInNew />}
        sx={{ mt: 2, textTransform: 'none', color: 'text.secondary' }}
        onClick={() => navigate('/app/recognitions')}
      >
        Ir para Cadastro de Usuários
      </Button>
    </Paper>
  );
}